/* eslint-disable metabase/no-color-literals */
import type { MetabaseThemeV2 } from "../../types";
import { LIGHT_THEME_ACCENT_COLORS } from "../accent-colors";
import { getBaseColorsForThemeDefinitionOnly } from "../base-colors";
import {
  SEMIPUBLIC_FILTER_COLOR,
  SEMIPUBLIC_SHADOW_COLOR,
  SEMIPUBLIC_COLORS as sp,
} from "../semipublic";

const baseColors = getBaseColorsForThemeDefinitionOnly();

// To be removed once the rename in GDGT-2517 is complete. Prefer the keys in
// `actualColors` below.
const deprecatedColors = {
  "admin-navbar": sp.forest,
  "admin-navbar-secondary": baseColors.brand[30],
  "admin-navbar-inverse": baseColors.brand[60],
  "background-brand": baseColors.brand[10],
  "background-filter": baseColors.filter[5],
  "background-disabled": baseColors.orionAlpha[10],
  "background-disabled-inverse": baseColors.orionAlphaInverse[10],
  "background-error-secondary": `color-mix(in srgb, ${sp.coral}, white 96%)`,
  "background-highlight":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background-selected":
    "color-mix(in srgb, var(--mb-color-core-brand) 21%, transparent)",
  "background-primary": baseColors.white,
  "background-secondary": sp["gray-100"],
  "background-tertiary": sp["gray-200"],
  "background-primary-inverse": sp.forest,
  "background-secondary-inverse": baseColors.orion[70],
  "background-tertiary-inverse": baseColors.orion[40],
  overlay: baseColors.orionAlpha[60],
  "background-error": `color-mix(in srgb, ${sp.coral}, white 91%)`,
  "background-success": sp["green-100"],
  "background-success-secondary": sp["green-200"],
  brand: sp.forest,
  "brand-hover": baseColors.brand[50],
  danger: sp["coral-600"],
  error: sp["coral-600"],
  filter: SEMIPUBLIC_FILTER_COLOR,
  focus: sp["green-300"],
  "icon-brand": "var(--mb-color-text-brand)",
  "icon-primary": "var(--mb-color-text-primary)",
  "icon-disabled": "var(--mb-color-text-disabled)",
  "icon-secondary": "var(--mb-color-text-secondary)",
  "illustration-brand-secondary": baseColors.brand[10],
  "illustration-brand-tertiary": baseColors.brand[5],
  "metabase-brand": baseColors.brand[40],
  "metabase-brand-hover": baseColors.brand[30],
  "saturated-blue": baseColors.ocean[60],
  "saturated-green": baseColors.palm[60],
  "saturated-purple": baseColors.octopus[60],
  "saturated-red": baseColors.lobster[60],
  "saturated-yellow": baseColors.dubloon[30],
  shadow: SEMIPUBLIC_SHADOW_COLOR,
  "success-secondary": sp["green-700"],
  success: sp["green-600"],
  summarize: sp["green-600"],
  "syntax-parameters-active": baseColors.mango[10],
  "syntax-parameters": baseColors.mango[60],
  "text-tertiary": sp["gray-500"],
  "text-tertiary-inverse": baseColors.orionAlphaInverse[40],
  "text-hover": baseColors.brand[60],
  "text-secondary-opaque": sp["gray-700"],
  warning: baseColors.dubloon[30],
  "background-warning": baseColors.dubloon[5],
  "background-warning-secondary": baseColors.dubloon[20],
  info: baseColors.orion[40],
  "background-info": baseColors.orion[10],
  white: baseColors.white,
  border: sp["gray-200"],
  "border-strong": sp["gray-300"],
  "border-stronger": sp["gray-400"],
  "border-subtle": baseColors.orionAlpha[10],
  gold: "#FFD700",
  "upsell-primary": baseColors.ocean[70],
  "upsell-secondary": baseColors.ocean[20],
  "upsell-gem": "#00d4ff",
};

const actualColors = {
  "background_page-filter": baseColors.filter[5],
  "background_page-highlighted":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background_page-primary": baseColors.white,
  "background_page-primary-inverse": sp.forest,
  "background_page-secondary": sp["gray-100"],
  "background_page-secondary-inverse": `color-mix(in srgb, ${baseColors.orion[100]}, black 25%)`,
  "background_page-tertiary": sp["gray-200"],
  "background_page-tertiary-inverse": `color-mix(in srgb, ${baseColors.orion[100]}, black 50%)`,
  "background_surface-brand-strong": baseColors.brand[60],
  "background_surface-brand-strong-hover": baseColors.brand[70],
  "background_surface-brand-strong-pressed": baseColors.brand[80],
  "background_surface-brand-subtle":
    "color-mix(in srgb, var(--mb-color-core-brand) 10%, transparent)",
  "background_surface-brand-subtle-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 30%, transparent)",
  "background_surface-brand-subtle-pressed":
    "color-mix(in srgb, var(--mb-color-core-brand) 60%, transparent)",
  "background_surface-disabled": baseColors.orionAlpha[10],
  "background_surface-disabled-inverse": baseColors.orionAlphaInverse[10],
  "background_surface-error": `color-mix(in srgb, ${sp.coral}, white 91%)`,
  "background_surface-error-subtle": `color-mix(in srgb, ${sp.coral}, white 96%)`,
  "background_surface-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background_surface-primary": "var(--mb-color-background_page-primary)",
  "background_surface-primary-hover": baseColors.orionAlpha[10],
  "background_surface-primary-inverse":
    "var(--mb-color-background_page-primary-inverse)",
  "background_surface-primary-inverse-hover": baseColors.orionAlphaInverse[20],
  "background_surface-primary-inverse-pressed":
    baseColors.orionAlphaInverse[10],
  "background_surface-primary-pressed": baseColors.orionAlpha[20],
  "background_surface-secondary": baseColors.orionAlpha[10],
  "background_surface-secondary-hover": baseColors.orionAlpha[20],
  "background_surface-secondary-inverse": baseColors.orionAlphaInverse[10],
  "background_surface-secondary-inverse-hover":
    baseColors.orionAlphaInverse[20],
  "background_surface-secondary-inverse-pressed":
    baseColors.orionAlphaInverse[10],
  "background_surface-secondary-pressed": baseColors.orionAlpha[30],
  "background_surface-selected":
    "color-mix(in srgb, var(--mb-color-core-brand) 21%, transparent)",
  "background_surface-success": sp["green-100"],
  "background_surface-warning": baseColors.dubloon[5],
  "background_surface-warning-strong": baseColors.dubloon[20],
  "border-brand": baseColors.brand[10],
  "border-filter": baseColors.filter[20],
  "border-neutral": sp["gray-200"],
  "border-neutral-strong": sp["gray-300"],
  "border-neutral-strongest": sp["gray-400"],
  "border-neutral-subtle": baseColors.orionAlpha[10],
  "core-blue-saturated": baseColors.ocean[60],
  "core-brand-hover": baseColors.brand[50],
  "core-gold": "#FFD700",
  "core-green-saturated": sp["green-600"],
  "core-info": baseColors.orion[40],
  "core-metabase_brand": baseColors.brand[40],
  "core-metabase_brand-hover": baseColors.brand[30],
  "core-purple-saturated": baseColors.octopus[60],
  "core-red-saturated": sp["coral-600"],
  "core-white": baseColors.white,
  "core-white_constant": "white",
  "core-yellow-saturated": baseColors.dubloon[30],
  "feedback-negative": sp["coral-600"],
  "feedback-negative-strong": `color-mix(in srgb, ${sp["coral-600"]}, black 20%)`,
  "feedback-neutral-strong": baseColors.orionAlpha[60],
  "feedback-positive": sp["green-600"],
  "feedback-positive-selected": sp["green-700"],
  "feedback-positive-strong": sp["green-700"],
  "feedback-warning": baseColors.dubloon[30],
  "feedback-warning-strong": baseColors.mango[60],
  "illustration-secondary-brand": baseColors.brand[10],
  "illustration-tertiary-brand": baseColors.brand[5],
  "input-focus": sp.green,
  "modal-overlay": baseColors.orionAlpha[60],
  "navbar-admin": sp.forest,
  "navbar-admin-inverse": baseColors.brand[60],
  "navbar-admin-secondary": baseColors.brand[30],
  "shadow-default": SEMIPUBLIC_SHADOW_COLOR,
  "switch-off": baseColors.orionAlpha[20],
  "text-brand": baseColors.brand[50],
  "text-brand-hover": baseColors.brand[60],
  "text-brand-strong": baseColors.brand[60],
  "text-brand-strong-hover": baseColors.brand[70],
  "text-disabled": sp["gray-400"],
  "text-disabled-inverse": baseColors.orionAlphaInverse[40],
  "text-filter": baseColors.filter[50],
  "text-primary": sp["gray-900"],
  "text-primary-inverse": baseColors.orionAlphaInverse[80],
  "text-secondary": sp["gray-700"],
  "text-secondary-inverse": baseColors.orionAlphaInverse[60],
  "text-selected": sp["gray-900"],
  "text-syntax-parameter": baseColors.mango[60],
  "text-syntax-parameter-active": baseColors.mango[10],
  "tooltip-background": sp.forest,
  "tooltip-background-focused": sp["forest-900"],
  "tooltip-text": baseColors.white,
  "tooltip-text-secondary": baseColors.orionAlphaInverse[60],
};

/**
 * The default light theme for Metabase.
 */
export const METABASE_LIGHT_THEME: MetabaseThemeV2 = {
  version: 2,
  chartColors: LIGHT_THEME_ACCENT_COLORS,
  colors: {
    ...deprecatedColors,
    ...actualColors,
  },
};
